/**
 * Component is used to show gift Wrap info in dialog on the cart page
 *
 * @attr {String} place-holder-text - placeholder for select
 * @attr {Number} max-length - max characters for message textarea
 * @attr {Boolean} emoji-sanitize - emoji sanitize textarea
 * @prop {String} giftOptionCode - gift option code
 * @prop {Object} appliedGiftOption - applied gift option
 *
 * HTML Structure Example
 * <e2-gift-option-message place-holder-text="please choose"></e2-gift-option-message>
 *
 */

import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import {
    COMPONENT_CONNECT,
    COMPONENT_DISCONNECT,
    createDispatcher,
} from '@elab/core/utils/dynaReduxHelpers';

import { actions, reducers, sagaFactory, selectors, storeName } from './redux';

class GiftOptionMessage extends SimpleLitElement {
    static get properties() {
        return {
            placeHolderText: { type: String, attribute: 'place-holder-text' },
            giftOptionCode: { type: String },
            appliedGiftOption: { type: Object },
            maxLength: { type: Number },
            isEmojiSanitize: { type: Boolean, attribute: 'emoji-sanitize' },
            errorLabel: { type: String, attribute: 'error-label' },
            showEmptyError: { type: Boolean, attribute: false },
            getEmptyMsgFeatureFlag: { type: Boolean, attribute: false },
            pattern: { type: String },
        };
    }

    constructor() {
        super();
        this.placeHolderText = '';
        this.appliedGiftOption = {};
        this.maxLength = 460;
        this.isEmojiSanitize = false;
        this.errorLabel = '';
        this.showEmptyError = false;
        this.getEmptyMsgFeatureFlag = false;
        this.pattern = '';
    }

    onInput = ({ detail }) => {
        this.dispatchEvent(
            new CustomEvent('update-wrap-message', {
                detail: {
                    message: detail,
                    giftOptionCode: this.giftOptionCode,
                },
            }),
        );
    };

    onKeyPress = detail => {
        if (this.pattern) {
            return detail.key && detail.key.match(this.pattern) ? true : detail.preventDefault();
        }
    };

    onPaste = ({ detail }) => {
        if (!this.pattern.match(detail)) {
            const val = detail
                .split('')
                .map(data => {
                    if (data.match(this.pattern)) {
                        return data;
                    }
                })
                .join('');

            this.onInput({ detail: val });
        }
    };

    render() {
        return html`
            <div class="gift-wrap__content">
                <e2-textarea
                    simple-character-counter
                    class="gift-wrap__textarea"
                    .value="${this.appliedGiftOption.message || ''}"
                    label="${this.placeHolderText}"
                    max-length="${this.maxLength}"
                    @change="${this.onInput}"
                    ?isEmojiSanitize="${this.isEmojiSanitize}"
                    @keypress="${this.onKeyPress}"
                    @onpaste="${this.onPaste}"
                    ?hasPatternConstraint="${this.pattern}"
                ></e2-textarea>
                ${this.getEmptyMsgFeatureFlag && this.showEmptyError
                    ? html`
                          <div class="gift-wrap__error">
                              ${this.errorLabel}
                          </div>
                      `
                    : null}
            </div>
        `;
    }

    afterConnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_CONNECT));
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_DISCONNECT));
    }
}

const mapStateToProps = state => {
    const { giftOptionCode } = selectors.getGiftOptionData(state);

    return {
        giftOptionCode,
        appliedGiftOption: selectors.getAppliedGiftOption(state, giftOptionCode),
        maxLength: selectors.getFieldConstraints(state).max,
        showEmptyError: selectors.getEmptyErrorMsg(state),
        getEmptyMsgFeatureFlag: selectors.getEmptyMsgFeatureFlag(state),
        pattern: selectors.getPatternConstraints(state).regex,
    };
};

const mapDispatchToEvents = dispatch =>
    createDispatcher(
        {
            storeName,
            reducers,
            sagaFactory,
            additionalEvents: {
                'update-wrap-message': ({ detail }) =>
                    dispatch(actions.updateAppliedOptionMessage(detail)),
            },
        },
        dispatch,
    );

export default () =>
    customElements.define(
        'e2-gift-option-message',
        connect(mapStateToProps, mapDispatchToEvents)(GiftOptionMessage),
    );
